import * as React from "react";
import { Check, Copy } from "lucide-react";
import * as Sentry from "@sentry/react";
import { copyTextToClipboard } from "@/helpers/clipboard";
import { StreamMessage, WebSocketMessage } from "@/modules/chat/chat-ui.types";
import type { Evidence } from "@/types/evidences";

interface Props {
  messages: StreamMessage[];
  allEvents: WebSocketMessage[];
  isStreaming: boolean;
  streamingMessageId: string | null;
  evidences: Evidence[];
}

function formatEvidences(evidences: Evidence[]): string {
  if (evidences.length === 0) return "";
  const lines = evidences.map(evidence => `Evidence ${evidence.citationId}: ${JSON.stringify(evidence)}`);
  return `Evidences:\n${lines.join("\n")}\n\n`;
}

export const CopyConversation: React.FC<Props> = ({
  messages,
  streamingMessageId,
  evidences,
  isStreaming,
  allEvents,
}) => {
  const [copiedConversation, setCopiedConversation] = React.useState(false);

  const copyConversation = React.useCallback(async () => {
    const parts: string[] = [];

    messages.forEach(message => {
      const sender = message.isUser ? "User" : "Assistant";
      parts.push(`${sender}: ${message.content}\n`);
      if (!message.isUser) {
        parts.push(formatEvidences(evidences));
      }
    });

    if (isStreaming && streamingMessageId) {
      const streamingMessage = messages.find(msg => msg.id === streamingMessageId);
      if (streamingMessage) {
        parts.push(`Assistant (streaming): ${streamingMessage.content}\n`);
        parts.push(formatEvidences(evidences));
      }
    }

    parts.push("All Events:\n");
    allEvents.forEach((event, idx) => {
      parts.push(`Event ${idx + 1} (${event.ev_type}): ${JSON.stringify(event)}\n`);
    });

    try {
      await copyTextToClipboard(parts.join("").trim());
      setCopiedConversation(true);
      setTimeout(() => setCopiedConversation(false), 2000);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [messages, evidences, isStreaming, allEvents, streamingMessageId]);

  return (
    <div className="mb-2 flex justify-end space-x-2">
      <button
        onClick={copyConversation}
        className="flex items-center rounded bg-white px-3 py-1 text-sm text-gray-500 transition-colors duration-200 hover:text-gray-700"
        title={copiedConversation ? "Copied!" : "Copy Entire Conversation"}
      >
        {copiedConversation ? <Check size={16} className="mr-1 text-green-500" /> : <Copy size={16} className="mr-1" />}
        {copiedConversation ? "Copied" : "Copy Conversation"}
      </button>
    </div>
  );
};
